import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, parse } from 'date-fns';
import { ru } from 'date-fns/locale';

import { crmApi, errMsg } from '@crm/api';
import { UiAutosuggest, UiButton, UiInput } from '@shared/common/ui';

function dayLabel(ymd) {
  const d = parse(ymd, 'yyyy-MM-dd', new Date());
  return `${format(d, 'dd.MM', { locale: ru })}\n(${format(d, 'EEE', { locale: ru })})`;
}

export function ClientsAttendanceSection({ branchId }) {
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [fio, setFio] = useState('');
  const [draftDirId, setDraftDirId] = useState('');
  const [draftDirLabel, setDraftDirLabel] = useState('');
  const [dirQuery, setDirQuery] = useState('');
  const [dirSuggestions, setDirSuggestions] = useState([]);
  const [page, setPage] = useState(0);
  const limit = 10;

  const [appliedFio, setAppliedFio] = useState('');
  const [appliedDirId, setAppliedDirId] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const headerScrollRef = useRef(null);

  useEffect(() => {
    if (!dirQuery.trim() || !branchId) {
      setDirSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      crmApi(branchId)
        .get('/directions', { params: { q: dirQuery.trim(), limit: 30 } })
        .then(({ data: d }) => setDirSuggestions(d.items ?? []))
        .catch(() => setDirSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [dirQuery, branchId]);

  const fetchGrid = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    const params = { month, page, limit };
    if (appliedFio.trim()) params.fio = appliedFio.trim();
    if (appliedDirId) params.directionId = appliedDirId;
    try {
      const { data: payload } = await crmApi(branchId).get('/clients/attendance-grid', { params });
      setData(payload);
    } catch (e) {
      setError(errMsg(e, 'Не удалось загрузить таблицу'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, month, page, limit, appliedFio, appliedDirId]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  useEffect(() => {
    if (data?.directions?.length) {
      setExpanded(new Set(data.directions.map((d) => d.id)));
    }
  }, [data?.directions]);

  const onApply = () => {
    setPage(0);
    setAppliedFio(fio);
    setAppliedDirId(draftDirId);
  };

  const onClear = () => {
    setFio('');
    setDraftDirId('');
    setDraftDirLabel('');
    setDirQuery('');
    setPage(0);
    setAppliedFio('');
    setAppliedDirId('');
  };

  const selectDir = (d) => {
    setDraftDirId(d.id);
    setDraftDirLabel(d.name);
    setDirQuery('');
  };

  const clearDir = () => {
    setDraftDirId('');
    setDraftDirLabel('');
  };

  const clearDirField = () => {
    clearDir();
    setDirQuery('');
  };

  const toggleDir = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageCount = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil((data.totalDirections ?? 0) / limit));
  }, [data, limit]);

  const syncHeaderScroll = (event) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const renderColGroup = () => (
    <colgroup>
      <col className="clients-attendance__col-fio" />
      {data.days.map((day) => (
        <col key={day} className="clients-attendance__col-day" />
      ))}
    </colgroup>
  );

  return (
    <div className="clients-attendance">
      <div className="clients-page__filter">
        <div className="clients-page__filter-title">Таблица посещений</div>
        <div className="clients-page__filter-grid">
          <UiInput
            label="Месяц"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            max={format(new Date(), 'yyyy-MM')}
          />
          <UiInput label="Ф.И.О." value={fio} onChange={(e) => setFio(e.target.value)} />
          <UiAutosuggest
            className="field clients-page__autosuggest"
            label="Направление"
            value={draftDirId ? draftDirLabel : dirQuery}
            onChange={(value) => {
              if (draftDirId) clearDir();
              setDirQuery(value);
            }}
            suggestions={dirSuggestions}
            onSelect={selectDir}
            placeholder="Все направления"
            onClear={clearDirField}
            emptyText=""
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.name}
          />
        </div>
        <div className="clients-page__filter-row">
          <UiButton type="button"  onClick={onApply}>
            Применить
          </UiButton>
          <UiButton variant="ghost" type="button"  onClick={onClear}>
            Очистить
          </UiButton>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {loading && <div className="placeholder">Загрузка…</div>}

      {!loading && data && (
        <div className="clients-page__table-wrap clients-attendance__wrap">
          <div className="clients-attendance__head-scroll" ref={headerScrollRef}>
            <table className="data-table clients-attendance__table clients-attendance__table--head">
              {renderColGroup()}
              <thead>
                <tr>
                  <th className="clients-attendance__fio">ФИО</th>
                  {data.days.map((day) => (
                    <th key={day} className="clients-attendance__day">
                      {dayLabel(day).split('\n').map((line, i) => (
                        <span key={i}>
                          {i > 0 && <br />}
                          {line}
                        </span>
                      ))}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>

          <div className="clients-attendance__body-scroll" onScroll={syncHeaderScroll}>
            <table className="data-table clients-attendance__table clients-attendance__table--body">
              {renderColGroup()}
              <tbody>
                {data.directions.map((dir) => (
                  <Fragment key={dir.id}>
                    <tr className="clients-attendance__dir-row">
                      <td colSpan={data.days.length + 1} className="clients-attendance__dir-name">
                        <button type="button" className="clients-attendance__dir-toggle" onClick={() => toggleDir(dir.id)}>
                          {expanded.has(dir.id) ? '▼' : '▶'} {dir.name}
                        </button>
                      </td>
                    </tr>
                    {expanded.has(dir.id) &&
                      dir.clients.map((cl) => (
                        <tr key={`${dir.id}-${cl.clientId}`}>
                          <td className="clients-attendance__fio">{cl.fio ?? '—'}</td>
                          {data.days.map((day) => {
                            const hasVisit = cl.visitDays.includes(day);
                            const hasPaid = cl.paidDays.includes(day);
                            const isSched = dir.scheduleDays.includes(day);
                            let cls = 'clients-attendance__cell';
                            if (hasVisit) cls += ' clients-attendance__cell--visit';
                            else if (hasPaid) cls += ' clients-attendance__cell--paid';
                            else if (isSched) cls += ' clients-attendance__cell--schedule';
                            return (
                              <td key={day} className={cls}>
                                {hasVisit ? '+' : ''}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </Fragment>
                ))}
                {data.directions.length === 0 && (
                  <tr>
                    <td colSpan={(data.days?.length ?? 0) + 1} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                      Нет данных за выбранный период
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="clients-page__pager">
        <UiButton variant="ghost" size="sm" type="button"  disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
          Назад
        </UiButton>
        <span className="clients-page__selected-hint">
          Стр. {page + 1} / {pageCount}
        </span>
        <UiButton variant="ghost" size="sm"
          type="button"
          
          disabled={page + 1 >= pageCount}
          onClick={() => setPage((p) => p + 1)}
        >
          Вперёд
        </UiButton>
      </div>
    </div>
  );
}
